<template>
	<div id="app">
		<component :is="currentBanner.banner" v-if="currentBanner" v-bind="currentBanner.props" />
		<ToastContainer />
		<RouterView :key="routeKey" />
		<transition name="transition--modal" mode="in-out">
			<component
				:is="currentModal.modal"
				v-if="currentModal"
				v-bind="currentModal.props"
				@modal-close="closeModal"
			/>
		</transition>
		<IntercomLauncher v-if="isIntercomEnabled && !consentStore.isDialogOpen"></IntercomLauncher>
	</div>
</template>

<script setup lang="ts">
import { MaybeComputedRef, Script, useHead } from '@unhead/vue';
import { useDebounceFn, useEventListener, useIdle } from '@vueuse/core';
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import IntercomLauncher from '@components/layout/intercom-launcher.vue';
import ToastContainer from '@components/layout/toast-container.vue';
import { appCloudfrontPath } from '@constants';
import { webviewRoutesTest } from '@constants/routes';
import { useAppStore } from '@stores/app';
import { useConsentStore } from '@stores/consent';
import { useThirdPartyAuthStore } from '@stores/third-party-auth';
import { initializeMixpanel, stopMixpanelTracking } from '@utils/analytics';
import { getScreenSize, isPreRender } from '@utils/client';
import { initDatadog, stopDatadog } from '@utils/datadog';
import { initIntercomWidget, registerIntercom, shutdownIntercom } from '@utils/intercom';
import { isSignUpRoute, stripRouteHash } from '@utils/routing';
import { addTrackers, removeTrackers } from '@utils/trackers';
import { CurrentBanner } from 'types/banner';
import { CurrentModal } from 'types/modal';
import appConfig from './app.config.json';

window.initMap = function () {
	// Intentionally left blank, callback is required. https://developers.google.com/maps/documentation/javascript/url-params#required_parameters
};

const route = useRoute();
const appStore = useAppStore();
const consentStore = useConsentStore();
const { idle } = useIdle(5 * 60 * 1000); // 5 min
const thirdPartyAuthStore = useThirdPartyAuthStore();
const prevWindowWidth = ref(-1);

const baseScripts = [
	{
		src: `https://accounts.google.com/gsi/client`,
		key: 'google-sign-in-script',
		async: true,
		onload: () => (thirdPartyAuthStore.isGoogleLibraryLoaded = true)
	},
	{
		src: `https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js`,
		key: 'apple-sign-in-script',
		async: true,
		onload: () => (thirdPartyAuthStore.isAppleLibraryLoaded = true)
	},
	{ src: 'https://cdn.plaid.com/link/v2/stable/link-initialize.js', key: 'plaid-script' }
];

const metaDescription = `Invest in real estate, venture capital, and private credit with Fundrise—America's largest direct-access alternative asset manager.`;

const isIntercomEnabled = computed((): boolean => appStore.intercomEnabled);

const currentModal = computed((): CurrentModal | null => {
	document.body.style.overflow = appStore.currentModal ? 'hidden' : '';
	return appStore.currentModal;
});

const currentBanner = computed((): CurrentBanner | null => appStore.currentBanner);

const routeKey = computed((): string => stripRouteHash(route));
const defaultHeadControl = {
	titleTemplate: (title?: string) => (title ? `${title} | ${appConfig.title}` : appConfig.title),
	link: [
		{ rel: 'canonical', href: window.location.origin + window.location.pathname },
		{
			rel: 'icon',
			type: 'image/png',
			href: `${appCloudfrontPath}/images/global/favicon.png`
		}
	],
	meta: [
		{ charset: 'utf-8' },
		{ name: 'viewport', content: 'width=device-width, minimum-scale=1.0, maximum-scale=1.0' },
		{ name: 'apple-mobile-web-app-capable', content: 'yes' },
		{ name: 'p:domain_verify', content: '411182b397074a11ab6dd421b34bc4e8' },
		{ property: 'og:type', content: 'website' },
		{ property: 'og:url', content: window.location.href },
		{ property: 'fb:app_id', content: appConfig.facebookAppId },
		{ name: 'twitter:card', content: 'summary_large_image' },
		{ name: 'twitter:site', content: '@fundrise' },
		{ name: 'description', content: metaDescription },
		{ property: 'og:description', content: metaDescription },
		{
			property: 'og:image',
			content: `${appCloudfrontPath + appConfig.metaImage}`
		}
	],
	script: baseScripts
};

const headControl = useHead(defaultHeadControl);

watch(idle, (idleValue) => {
	appStore.userIsIdle = idleValue;
});

async function isUserwayEnabled(isMobileWebviewRoute: boolean): Promise<boolean> {
	const featureEnabled = await appStore.isFeatureEnabled('userway-accessibility-plugin');
	const isPreRenderUserAgent = isPreRender();
	const isDataViz = window.location.pathname.startsWith('/data-visualization') || route.name === 'data-visualization';
	return featureEnabled && !isPreRenderUserAgent && !isDataViz && !isMobileWebviewRoute;
}

async function initializeScripts(isMobileWebviewRoute: boolean): Promise<void> {
	const scripts: MaybeComputedRef<Script<['script']>[]> = [...baseScripts];

	const userwayEnabled = await isUserwayEnabled(isMobileWebviewRoute);
	if (userwayEnabled) {
		scripts.push({
			src: 'https://cdn.userway.org/widget.js',
			key: 'userway-script',
			async: true,
			id: 'userway',
			'data-account': 'cVMkm5shQu'
		});
	}

	headControl?.patch({
		...defaultHeadControl,
		script: scripts
	});
}

function updateScreenSize(): void {
	if (window.innerWidth === prevWindowWidth.value) return;

	prevWindowWidth.value = window.innerWidth;
	appStore.updateScreenSize(getScreenSize());
}

function closeModal(): void {
	appStore.updateCurrentModal(null);
}

// Note - This is manually mounted. It will run before the route guards.
async function setup(): Promise<void> {
	updateScreenSize();
	useEventListener(window, 'load', useDebounceFn(updateScreenSize, 200));
	useEventListener(window, 'resize', useDebounceFn(updateScreenSize, 200));

	const params = new URLSearchParams(window.location.search);
	const webviewParam = params.get('webview');
	const isMobileWebviewRoute = webviewRoutesTest.test(window.location.pathname) || webviewParam === 'true';
	if (isMobileWebviewRoute) {
		appStore.isMobileWebview = true;
	}

	if (isMobileWebviewRoute) {
		initializeMixpanel();
		initDatadog();
	} else {
		consentStore.initialize();

		watch(
			() => consentStore.hasAnalyticsConsent,
			(allowed) => {
				if (allowed) {
					initializeMixpanel();
					initDatadog();
				} else {
					stopMixpanelTracking();
					stopDatadog();
				}
			},
			{ immediate: true }
		);

		watch(
			() => consentStore.hasPersonalizationConsent,
			async (allowed) => {
				if (allowed) {
					initIntercomWidget();
					await registerIntercom(isSignUpRoute(route.path) ? 'SIGN_UP' : 'STANDARD', route);
				} else {
					await shutdownIntercom();
				}
			},
			{ immediate: true }
		);

		watch(
			() => consentStore.hasMarketingConsent,
			(allowed) => {
				if (import.meta.env.VITE_ENABLE_CONVERSION_PIXELS === 'true') {
					if (allowed) addTrackers();
					else removeTrackers();
				}
			},
			{ immediate: true }
		);
	}

	await appStore.initializeLaunchDarklyInstance();
	await initializeScripts(isMobileWebviewRoute);
}

setup();
</script>
